import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserSession } from '../contexts/UserContext.js';
import { isDevelopment } from '../utils/environment.js';
import logo from '../../src/logo2.svg';
import back from '../Ui/back.webp';
import TopMenu from './topMenu.js';
import DashboardButton from './DashboardButton.js';
import Chat from './Chat.js';
import GodManagement from './GodPanel.js';
import Profile from './Profile.js';
import Feedback from './Feedback.js';
import Settings from './Settings.js';
import FomoOverlay from './FomoOverlay.js';
import loading from '../Ui/loading.gif';
import imgChat from '../Ui/chat.webp';
import imgAttach from '../Ui/attached.webp';
import imgMic from '../Ui/microphone.webp';
import imgSettings from '../Ui/adjust.webp';
import imgWill from '../Ui/address-book.webp';
import './Home.css';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, isLoading } = useAuth0();
  const isSignedIn = !!isAuthenticated;
  const isLoaded = !isLoading;
  const { userSession, hasElevatedAccess } = useUserSession();
  const [activeView, setActiveView] = useState(location.state?.initialView || 'dashboard');
  const [fomoOverlay, setFomoOverlay] = useState({ isVisible: false, featureName: '' });

  // Check if we're on localhost for dev mode - STANDARDIZED PATTERN
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();


  const handleViewChange = (view) => {
    // Paid users can only access profile/settings/feedback
    // Elevated users can access everything
    const paidOnlyViews = ['profile', 'settings', 'feedback'];
    
    // If paid user clicks home button, send them back to landing
    if ((view === 'dashboard' || view === '') && !hasElevatedAccess() && !allowDevBypass) {
      window.location.href = '/landing';
      return;
    }
    
    // If user is trying to access a non-paid view and they're not elevated
    if (!paidOnlyViews.includes(view) && !hasElevatedAccess() && !allowDevBypass) {
      // Show FOMO overlay instead of navigating
      const featureNames = {
        'profile': 'Profile Management',
        'settings': 'Account Settings', 
        'feedback': 'Feedback System'
      };
      
      setFomoOverlay({
        isVisible: true,
        featureName: featureNames[view] || 'Premium Feature'
      });
      return;
    }
    
    setActiveView(view);
  };
  
  const closeFomoOverlay = () => {
    setFomoOverlay({ isVisible: false, featureName: '' });
  };
  
  if (!isLoaded) {
    return <div className="loading-container"><br/><br/><br/><img src={loading} className="loading-gif" alt="loading..." /></div>;
  }

  const renderView = () => {
    switch (activeView) {
      case 'chat':
        return <Chat goBack={() => handleViewChange('dashboard')} user={authUser || userSession} />;
      case 'god':
        return <GodManagement goBack={() => handleViewChange('dashboard')} />;
      case 'profile':
        return <Profile goBack={() => handleViewChange('dashboard')} />;
      case 'feedback':
        return <Feedback goBack={() => handleViewChange('dashboard')} />;
      case 'settings':
        return <Settings goBack={() => handleViewChange('dashboard')} />;
      case 'dashboard':
      default:
        return (
          <>
          <div className='mainPageContainer'>
            <div className='Main-page-header'>
          <button className="back-btn" onClick={() => navigate('/')}><img src={back} className='backBtnImg' alt="back"></img></button>
            </div>
            <div className="button-grid">
              <DashboardButton
                title="Communication"
                subtitle="Teach your speech"
                icon={imgChat}
                onClick={() => handleViewChange('chat')}
              />

              <DashboardButton
                title="Attach Media"
                subtitle="Create your life's library"
                icon={imgAttach}
                onClick={() => handleViewChange('')}
              />

              <DashboardButton
                title="Voice Replication"
                subtitle="Clone your voice"
                icon={imgMic}
                onClick={() => handleViewChange('')}
              />
              
              <DashboardButton
                title="Preferences"
                subtitle="Your personal settings"
                icon={imgSettings}
                onClick={() => handleViewChange('')}
              />
              
              <DashboardButton
                title="Upload Will"
                subtitle="Store latest version"
                icon={imgWill}
                onClick={() => handleViewChange('')}
              />
            </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="App">
      <TopMenu handleViewChange={handleViewChange} />
      <header className="App-header">
        { (allowDevBypass && hasElevatedAccess()) || (isSignedIn && authUser) ? renderView() : (
          <>
            <img src={logo} className="App-logo" alt="logo" />
            <br/>
            <p>Welcome to the Intellegacy</p>
          </>
        )}
      </header>
      <FomoOverlay 
        isVisible={fomoOverlay.isVisible}
        onClose={closeFomoOverlay}
        featureName={fomoOverlay.featureName}
      />
    </div>
  );
};

export default Home;